import React from 'react';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight } from 'lucide-react';

interface ChecklistStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface OnboardingWizardStepProps {
  step: ChecklistStep;
  index: number;
  isCurrentStep: boolean;
  isPastStep: boolean;
  isFutureStep: boolean;
  onStepComplete: (stepId: string) => void;
}

/**
 * Individual wizard step component for onboarding checklist
 * 
 * Displays a single step in the linear onboarding wizard with:
 * - Visual state indicators (past, current, future)
 * - Step number and completion status
 * - Action buttons for current step
 * - Proper accessibility and responsive design
 */
const OnboardingWizardStep: React.FC<OnboardingWizardStepProps> = ({
  step,
  index,
  isCurrentStep,
  isPastStep,
  isFutureStep,
  onStepComplete
}) => {
  return (
    <div
      className={`relative flex items-center p-4 rounded-lg border-2 transition-all duration-200 ${
        isPastStep
          ? 'border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800'
          : isCurrentStep
          ? 'border-primary bg-primary/5 dark:bg-primary/10'
          : 'border-gray-200 bg-gray-50 dark:bg-gray-900 dark:border-gray-700'
      }`}
    >
      {/* Step Number Circle */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-4 ${
        isPastStep
          ? 'bg-green-500 text-white'
          : isCurrentStep
          ? 'bg-primary text-white'
          : 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
      }`}>
        {isPastStep ? (
          <CheckCircle className="h-4 w-4" />
        ) : (
          <span className="text-sm font-semibold">{index + 1}</span>
        )}
      </div>

      {/* Step Content */}
      <div className="flex-grow">
        <div className="flex items-center gap-2 mb-1">
          <div className={`${
            isPastStep ? 'text-green-600' : isCurrentStep ? 'text-primary' : 'text-gray-400'
          }`}>
            {step.icon}
          </div>
          <h3 className={`font-medium ${
            isPastStep ? 'text-green-800 dark:text-green-200' : 
            isCurrentStep ? 'text-gray-900 dark:text-gray-100' : 
            'text-gray-500 dark:text-gray-400'
          }`}>
            {step.title}
          </h3>
        </div>
        <p className={`text-sm ${
          isPastStep ? 'text-green-700 dark:text-green-300' :
          isCurrentStep ? 'text-gray-600 dark:text-gray-300' : 
          'text-gray-400 dark:text-gray-500'
        }`}>
          {step.description}
        </p>
      </div>

      {/* Action Button - Only for current step */}
      {isCurrentStep && step.action && (
        <div className="flex-shrink-0 ml-4">
          <Button
            size="sm"
            className="bg-primary hover:bg-primary/90"
            onClick={step.action.onClick}
          >
            {step.action.label}
            <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Manual Complete Button (for demo purposes) - Only for current step */}
      {isCurrentStep && (
        <div className="flex-shrink-0 ml-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onStepComplete(step.id)}
          >
            Skip
          </Button>
        </div>
      )}

      {/* Completed Indicator */}
      {isPastStep && (
        <div className="flex-shrink-0 ml-4 text-green-600 text-sm font-medium flex items-center">
          <CheckCircle className="h-4 w-4 mr-1" />
          Done
        </div>
      )}
    </div>
  );
};

export default OnboardingWizardStep;